import * as React from "react";

import { drawImageProp } from "@/util/CanvasUtils";
import { noop } from "@/util/Util";
import { PHOTOGRAPH_WIDTH_PX, PHOTOGRAPH_HEIGHT_PX } from "@/data/Config";

import ColorSelectorBG from "@/assets/img/layout/game/canvas-color-selector-bg.svg";

import TrashCan from "@/assets/img/symbolic/caption-trash.svg?react";
import {
    renderTextBlock,
} from "./CanvasUtils";
import { sanitizeTextBlock } from "@/realize-shared/Sanitization";
import {
    safeAreaBelow,
} from "@/util/Insets";

export interface HeadlineCanvasRef
{
    getRenderedOutput: () => Promise<string>;
}

const CaptionColors = [
    "#FFFFFF",
    "#000000",
    "#1DCEF9",
    "#0DD118",
    "#F8C604",
    "#F03D26",
    "#FC4D96",
    "#FA89DB",
    "#783BF5",
];

let bakedCaption: HTMLImageElement | null;

// const textTop = textInnerHeight / 2;
// const textBottom = textTop + textInnerHeight;

const render = (
    canvas: HTMLCanvasElement,
    img: HTMLImageElement,
    {
        captionY,
        captionText,
        bakedCaptionImage,
        showCaptionEditor,
    }: {
        showCaptionEditor: boolean;
        captionText: string;
        captionY: number;
        captionColor: string;
        bakedCaptionImage: HTMLImageElement | null;
    },
) =>
{
    canvas.width = PHOTOGRAPH_WIDTH_PX;
    canvas.height = PHOTOGRAPH_HEIGHT_PX;

    const ctx = canvas.getContext("2d", {
        "alpha": false,
    });

    if (!ctx) throw new Error("Cannot acquire 2d render context");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw the image in css cover mode
    drawImageProp(ctx, img);

    const scale = canvas.width / document.body.clientWidth;

    if (bakedCaptionImage)
    {
        const offsetY = 0;

        if (captionText.trim() !== "" && !showCaptionEditor)
        {
            ctx.drawImage(
                bakedCaptionImage,
                0,
                (captionY * scale) - (bakedCaptionImage.height / 2) - offsetY,
            );
        }
    }
};

const cacheTextRender = async (text: string, captionColor: string) =>
{
    bakedCaption = await renderTextBlock(text, captionColor);
};

const SMALL_DELAY = 100;
const BOTTOM_THRESHOLD = 16;
const QUALITY = 0.8;

/**
 * This is the canvas that is used to render the caption editor
 * @param props The props for the caption Editor
 */
export const CaptionCanvas = React.forwardRef(({
    showCaptionEditor,
    showPositioning,
    imageData,
    setIsEditing,
    setIsPositioning,
}: {
    showCaptionEditor: boolean;
    showPositioning: boolean;
    imageData: string;
    setIsEditing: (isEditing: boolean) => void;
    setIsPositioning: (isPositioning: boolean) => void;
}, ref: React.Ref<HeadlineCanvasRef>) =>
{
    const halfScreenHeight = (window.innerHeight / 2);

    const [captionY, setCaptionY] = React.useState(halfScreenHeight);
    const [captionText, setCaption] = React.useState("");
    const [captionColor, setCaptionColor] = React.useState("#FFFFFF");
    const [img, setImage] = React.useState(new Image());

    const inputRef = React.useRef<HTMLTextAreaElement>(null);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [startY, setStartY] = React.useState(-1);
    const [startCaptionY, setStartCaptionY] = React.useState(captionY);
    const [difference, setDifference] = React.useState(-1);

    const touchStart = React.useCallback((e: TouchEvent) =>
    {
        if (startY === -1)
        {
            setStartY(e.touches[0].clientY);
            setStartCaptionY(captionY);
        }
    }, [captionY, startY]);

    const touchEnd = React.useCallback(() =>
    {
        if (Math.abs(difference) > differenceThreshold)
        {
            Logger.info(startCaptionY, captionY);
            setIsEditing(false);
            setIsPositioning(false);
        }

        setStartY(-1);
        setDifference(-1);
    }, [captionY, difference, setIsEditing, setIsPositioning, startCaptionY]);

    const differenceThreshold = 10;

    const touchMove = React.useCallback((e: TouchEvent) =>
    {
        if (startY === -1) return;

        if (captionText.trim().length === 0) return;

        setDifference(startY - e.touches[0].clientY);

        if (Math.abs(difference) > differenceThreshold)
        {
            if (showCaptionEditor)
            {
                setIsEditing(false);

                if (inputRef.current?.value.trim() === "")
                {
                    setIsPositioning(false);
                }
                else
                {
                    setIsPositioning(true);
                }
            }

            if (!showCaptionEditor && !showPositioning && captionText.trim().length > 0)
            {
                setIsEditing(false);
                setIsPositioning(true);
            }

            const canvas = canvasRef.current;

            if (!canvas) return;

            if (e instanceof MouseEvent)
            {
            // Get the Y position of the touch from the top of the canvas
                const touchY = e.clientY - canvas.offsetTop;

                // Set the captionY to the touchY
                setCaptionY(touchY);
            }
            else
            {
            // Get the Y position of the touch from the top of the canvas
                const touchY = e.touches[0].clientY - canvas.offsetTop;

                // Set the captionY to the touchY
                setCaptionY(touchY);
            }
        }
    }, [captionText, difference, setIsEditing, setIsPositioning, showCaptionEditor, showPositioning, startY]);

    const bindContainer = React.useCallback((container: HTMLElement | null) =>
    {
        if (container === null) return;

        container.addEventListener("touchstart", touchStart);
        container.addEventListener("touchend", touchEnd);
        container.addEventListener("touchmove", touchMove);

        // container.addEventListener("mousedown", touchStart);
        // container.addEventListener("mousemove", touchMove);
        // container.addEventListener("mouseup", touchEnd);
    }, [touchEnd, touchMove, touchStart]);

    // const unbindContainer = (container: HTMLElement|null) =>
    // {
    //     if (container === null) return;

    //     container.removeEventListener("touchstart", touchStart);
    //     container.removeEventListener("touchend", touchEnd);
    //     container.removeEventListener("touchmove", touchMove);
    //     container.removeEventListener("mousedown", touchStart);
    //     container.removeEventListener("mousemove", touchMove);
    //     container.removeEventListener("mouseup", touchEnd);
    // };

    React.useEffect(() =>
    {
        // @FIXME this feels extremely fragile. We should re-evaluate
        if (img.src === imageData)
        {
            const canvas = canvasRef.current;

            if (!canvas) return;

            render(canvas, img, {
                showCaptionEditor,
                captionText,
                captionY,
                captionColor,
                "bakedCaptionImage": bakedCaption,
            });
        }
        else
        {
            setImage(() =>
            {
                const image = new Image();

                image.onload = () =>
                {
                    const canvas = canvasRef.current;

                    if (!canvas) return;

                    render(canvas, image, {
                        showCaptionEditor,
                        captionText,
                        captionY,
                        captionColor,
                        "bakedCaptionImage": bakedCaption,
                    });
                };

                image.src = imageData;

                return image;
            });
        }
    }, [canvasRef, imageData, captionY, showCaptionEditor, img, captionText, captionColor]);

    React.useEffect(() =>
    {
        const canvas = canvasRef.current;

        if (!canvas) return;

        cacheTextRender(captionText, captionColor).catch(Logger.error);
    }, [canvasRef, captionText, captionColor]);

    React.useEffect(() =>
    {
        if (!canvasRef.current) throw new Error("Canvas not accessible");

        if (!showCaptionEditor)
        {
            // Sanitize the caption's white-space
            const cleanCaption = sanitizeTextBlock(captionText);

            // Set the caption to the clean caption
            setCaption(cleanCaption);
        }
    }, [canvasRef, captionText, showCaptionEditor]);

    React.useEffect(() =>
    {
        if (startY === -1) return;

        if (canvasRef.current)
        {
            const endThreshold = canvasRef.current.clientHeight - BOTTOM_THRESHOLD;

            // // Check if caption Y is near the bottom 3 rem of the screen
            if (captionY > endThreshold)
            {
                // Erase the caption
                setCaption("");
                setIsPositioning(false);
            }
        }
    }, [captionY, setIsPositioning, startY]);

    React.useImperativeHandle(ref, () => ({
        async getRenderedOutput (): Promise<string>
        {
            if (!canvasRef.current) throw new Error("Canvas not accessible");

            // @FIXME: is this function async for a reason? It doesn't have to be
            return Promise.resolve(canvasRef.current.toDataURL("image/jpeg", QUALITY));
        },
    }), [canvasRef]);

    React.useEffect(() =>
    {
        // Bind Mouse/Touch Events of Canvas
        const canvas = canvasRef.current;

        if (canvas)
        {
            canvas.style.height = `${document.body.clientWidth * (nineBySixteen)}px`;
            bindContainer(canvas);

            // bindContainer(containerRef.current as HTMLElement);

            // return () =>
            // {
            //     unbindContainer(canvas);
            //     unbindContainer(containerRef.current as HTMLElement);
            // };
        }

        if (showPositioning || showCaptionEditor)
        {
            // Hide Chat
            document.body.classList.add("pop-over");

            // Resize the text area
            if (inputRef.current)
            {
                adjustTextAreaHeight(inputRef.current);
            }
        }
        else
        {
            // Show Chat
            document.body.classList.remove("pop-over");
        }


        return noop;
    }, [bindContainer, showCaptionEditor, showPositioning]);

    const nineBySixteen = 1.777777778;

    const getKeyboardHeight = () =>
    {
        return parseInt(
            getComputedStyle(document.documentElement).getPropertyValue("--keyboard-height"),
            10,
        );
    };

    React.useEffect(() =>
    {
        const setCaptionYOnResize = () =>
        {
            Logger.log("Keyboard Shown");

            Logger.info("Keyboard Height", getKeyboardHeight());

            const halfScreenKeyboard = (document.body.clientHeight - getKeyboardHeight()) / 2;

            setCaptionY(halfScreenKeyboard);
        };

        window.addEventListener("InsetsChanged", setCaptionYOnResize);

        return () =>
        {
            window.removeEventListener("InsetsChanged", setCaptionYOnResize);
        };
    }, []);

    const finishTyping = () =>
    {
        setTimeout(() =>
        {
            inputRef.current?.blur();
        }, SMALL_DELAY);
    };

    const focusInput = () =>
    {
        const textarea = inputRef.current;

        if (textarea)
        {
            textarea.focus({
                "preventScroll": true,
            });
        }
    };

    const adjustTextAreaHeight = (textArea: HTMLTextAreaElement) =>
    {
        textArea.style.height = "0px";
        textArea.style.height = `${textArea.scrollHeight}px`;
    };

    const rem1 = 16;

    const bottomOfColorPicker = safeAreaBelow() + rem1 + getKeyboardHeight();

    Logger.info("Bottom of Color Picker", bottomOfColorPicker);

    return (
        <div
            className={Wind.style({
                width: "w-full",
                height: "h-full",
                textAlign: "text-center"
            }).class}
            style={{
                marginTop: "calc(var(--top-nav-offset) - var(--safe-area-top))"
            }}
        >
            <canvas
                id="photo-canvas"
                ref={canvasRef}
                className={Wind.style({
                    maxWidth: "max-w-full",
                    aspectRatio: "aspect-[9/16]",
                    position: "relative",
                    overflow: "overflow-clip",
                    display: "inline-block",
                    borderRadius: "rounded-2xl"
                }).class}
                onClick={(e) =>
                {
                    if (e.currentTarget === canvasRef.current)
                    {
                        Logger.log("Clicked on canvas");

                        if (showCaptionEditor)
                        {
                            Logger.info("Clicked on canvas while editing");
                            setIsEditing(false);
                        }
                        else
                        {
                            Logger.info("Not Editing");

                            if (!showPositioning)
                            {
                                setIsEditing(true);

                                setTimeout(() =>
                                {
                                    focusInput();
                                }, SMALL_DELAY);
                            }
                        }

                        // if (showCaptionEditor)
                        // {

                        //     Logger.info("Clicked on canvas while editing");
                        //     setIsEditing(false);
                        // }
                        // else
                        // {
                        //     setIsEditing(true);
                        //     setTimeout(() =>
                        //     {
                        //         focusInput();
                        //     }, SMALL_DELAY);
                        // }
                    }
                }}
            />
            {showCaptionEditor && !showPositioning && (
                <>
                    <div
                        ref={containerRef}
                        className={Wind.style({
                            position:"absolute",
                            left: "left-0",
                            right: "right-0",
                            flexDirection: "flex-col",
                            display: "flex"
                        }).class}
                        style={{
                            "top": `${captionY}px`,
                        }}
                    >
                        <div />
                        <textarea
                            ref={inputRef}
                            placeholder="Enter Caption"
                            className={Wind.style({
                                backgroundColor: "bg-black",
                                backgroundOpacity: "bg-opacity-50",
                                textAlign: "text-center",
                                fontWeight: "font-bold",
                                fontFamily: "font-default",
                                fontSize: "text-xl",
                                width: "w-full",
                                minHeight: "min-h-8",
                                height: "h-auto",
                                transformTranslateY: "-translate-y-1/2",
                                ":focus": {
                                    outlineStyle: "focus:outline-none"
                                },
                                display: "block",
                                // @ts-expect-error https://github.com/danpacho/tailwindest/issues/123
                                whitespace: "whitespace-break-spaces"
                            }).class}
                            style={{
                                "color": captionColor,
                            }}
                            value={captionText}
                            onChange={(e) =>
                            {
                                setCaption(e.target.value);
                            }}

                            onClick={(e) =>
                            {
                                e.stopPropagation();
                                e.preventDefault();
                            }}

                            onKeyUp={(e) =>
                            {
                                if (e.key === "Enter")
                                {
                                    e.preventDefault();
                                    setIsEditing(false);
                                    finishTyping();
                                }

                                adjustTextAreaHeight(e.currentTarget);
                            }}
                        />
                    </div>
                    <div
                        className={Wind.style({
                            width: "w-full",
                            aspectRatio: "aspect-[414/76]",
                            height: "h-auto",
                            backgroundPosition: "bg-center",
                            backgroundSize: "bg-cover",
                            display: "flex",
                            gap: "gap-4",
                            alignItems: "items-center",
                            justifyContent: "justify-center",
                            position: "fixed",
                            transitionProperty: "transition-all"
                        }).class}
                        style={{
                            "backgroundImage": `url(${ColorSelectorBG})`,
                            "bottom":          `${bottomOfColorPicker}px`,
                        }}
                    >
                        {CaptionColors.map((color, i) => (
                            <div
                                key={i}
                                className={Wind.style({
                                    width: "w-6",
                                    height: "h-6",
                                    borderRadius: "rounded-full",
                                    display: "inline-block",
                                    transformTranslateY: "-translate-y-1",
                                    borderWidth: "border-2",
                                    borderColor: color === captionColor ? "border-dark-blue" : "border-transparent",
                                    // @ts-expect-error looks like this is a bug
                                    boxShadow: "shadow-[0px_0px_0px_2px_rgb(1,25,54),0px_0px_0px_4px_#FFFFFF]",
                                }).class}
                                style={{
                                    "backgroundColor": color,
                                }}
                                onClick={() =>
                                {
                                    setCaptionColor(color);

                                    focusInput();
                                }}
                            />
                        ))}
                    </div>
                    <div
                        className="fixed right-0 top-[calc(var(--safe-area-top))]"
                    >
                        <button
                            className={Wind.style({
                                paddingX: "px-4",
                                paddingY: "py-4",
                                fontWeight: "font-semibold",
                                fontFamily: "font-default",
                                color: "text-white",
                                fontSize: "text-base"
                            }).class}
                            style={{
                                "textShadow": "0px 2px 4px rgba(0,0,0,0.5)",
                            }}
                            onClick={() =>
                            {
                                setIsEditing(false);
                            }}
                        >
                            Done
                        </button>
                    </div>
                </>
            )}
            {showPositioning && (
                <div
                    className={Wind.style({
                        position: "absolute",
                        bottom: "bottom-0",
                        left: "left-0",
                        right: "right-0",
                        height: "h-16",
                        zIndex: "z-[1]",
                        display: "flex",
                        alignItems: "items-center",
                        justifyContent: "justify-center"
                    }).class}
                >
                    <TrashCan className="w-8 h-8" />
                </div>
            )}
        </div>
    );
});

CaptionCanvas.displayName = "HeadlineCanvas";
